/**
 * Document (PDF) limits.
 *
 * These mirror the server-side limits enforced by the PDS in
 * `api/app/bsky/util/documentProcessor.ts`. The composer validates against
 * them so users get instant feedback, and the server remains the source of
 * truth for anything that bypasses the client.
 */

export const PDF_MIME_TYPE = 'application/pdf'

/**
 * Maximum document size, in mebibytes to match the PDS.
 */
export const DOCUMENT_MAX_SIZE = 20 * 1024 * 1024 // 20 MiB

/**
 * Maximum number of pages in a PDF document.
 */
export const PDF_MAX_PAGES = 150

/**
 * Maximum document title length, in characters. The lexicon allows 300
 * graphemes, and a grapheme is never shorter than a character, so capping by
 * characters keeps an edited title inside the record limit.
 */
export const DOCUMENT_TITLE_MAX_LENGTH = 300

/**
 * Material caps — the audio and questions a *page* may carry.
 *
 * One audio and five questions, declared here rather than in the sheet that
 * enforces them, because three places need to agree: the sheet stops the author
 * from adding another, the pure model trims anything that arrives over the cap
 * (a record written by an older or a hostile client), and the report counts
 * against the same number. A cap that lived in the sheet alone would be a
 * display rule rather than a rule.
 */
export const DOCUMENT_PAGE_MAX_AUDIO = 1
export const DOCUMENT_PAGE_MAX_QUESTIONS = 5

/**
 * How long a question's own words may be.
 *
 * Generous on purpose, and generous rather than absent: an over-long prompt is
 * truncated by the model instead of dropping the question, because a long
 * sentence is the author's writing rather than an error. What these bound is the
 * *record*: 150 pages × 5 questions × these lengths is the worst case, and
 * {@link MATERIAL_MAX_JSON_BYTES} is what that worst case has to fit inside.
 */
export const MATERIAL_QUESTION_MAX_PROMPT_LENGTH = 300
export const MATERIAL_QUESTION_MAX_OPTION_LENGTH = 120
export const MATERIAL_QUESTION_MAX_OPTIONS = 6
export const MATERIAL_QUESTION_MAX_EXPLANATION_LENGTH = 400

/**
 * The largest a material record may be, in JSON characters.
 *
 * A PDS accepts a write up to about a megabyte, and this is deliberately under
 * half of that: the record rides through an `applyWrites` beside the post, both
 * are hashed locally, and a deck that *nearly* fits is not a deck that fits once
 * it has been encoded. Material over the budget is trimmed from the last page
 * backwards, which keeps the earlier pages whole — see
 * `#/lib/document/material`.
 */
export const MATERIAL_MAX_JSON_BYTES = 480 * 1024

/**
 * The largest a *draft's* own body may be, in JSON characters.
 *
 * A draft is one JSON request body, and the app view parses those with a 100 KB
 * limit (`payload.jsonLimit` in `packages/bsky`), so this is deliberately well
 * under half of that: the same body also carries every post's text, its labels
 * and its other embeds, and a draft that is *nearly* accepted is not accepted.
 *
 * The number is only ever compared against the page activity, because that is
 * the one part of a document draft that is unbounded — a document's own record
 * budget ({@link MATERIAL_MAX_JSON_BYTES}) is five times this, so a deck whose
 * questions are comfortably publishable can still be too large to *keep*. What
 * happens then is trimming, from the last page backwards, and the author is told
 * which pages were left out rather than losing them quietly.
 */
export const DRAFT_MAX_JSON_BYTES = 64 * 1024

/**
 * Reserved inside {@link DRAFT_MAX_JSON_BYTES} for what wraps the material.
 *
 * The budget is measured against the material's own JSON, while the limit is on
 * the whole body, so the difference — the embed's own keys, the `$type`s and the
 * escaping — is kept aside rather than raced.
 */
export const DRAFT_EMBED_HEADROOM_BYTES = 1024

export const SUPPORTED_DOCUMENT_MIME_TYPES = [
  PDF_MIME_TYPE,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/markdown',
] as const

export type DocumentValidationIssue =
  /** MIME type is not supported at all */
  | 'unsupported'
  /** File is larger than {@link DOCUMENT_MAX_SIZE} */
  | 'tooLarge'
  /** PDF has more pages than {@link PDF_MAX_PAGES} */
  | 'tooManyPages'

/**
 * Cleans up an edited document title, or returns `null` when there is nothing
 * publishable left — the embed record always carries a title, so a blank one is
 * treated as "no change" rather than as an empty title.
 */
export function normalizeDocumentTitle(title: string): string | null {
  const trimmed = title.trim().slice(0, DOCUMENT_TITLE_MAX_LENGTH)
  return trimmed ? trimmed : null
}

export function isPdf(mimeType: string): boolean {
  return mimeType === PDF_MIME_TYPE
}

export function isSupportedDocumentMimeType(mimeType: string): boolean {
  return (SUPPORTED_DOCUMENT_MIME_TYPES as readonly string[]).includes(mimeType)
}

/**
 * Pure validation of document metadata, so it can be unit tested without a
 * real file or a PDF parser. `pageCount` is only checked for PDFs, since we
 * cannot count pages of the other formats client-side.
 */
export function validateDocumentMeta({
  mimeType,
  size,
  pageCount,
}: {
  mimeType: string
  size: number
  pageCount?: number
}): DocumentValidationIssue | null {
  if (!isSupportedDocumentMimeType(mimeType)) {
    return 'unsupported'
  }
  if (size > DOCUMENT_MAX_SIZE) {
    return 'tooLarge'
  }
  if (isPdf(mimeType) && pageCount !== undefined && pageCount > PDF_MAX_PAGES) {
    return 'tooManyPages'
  }
  return null
}
